import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { HeaderComponent, TableComponent } from "Components/Shared";
import { Button } from "Components/Core";
import {
  getCoachVerificationRequests,
  updateCoachVerificationRequests,
} from "Api/Pages/CoachesApi";
import { useNavigate, useLocation } from "react-router-dom";

type CoachVR = {
  id: number;
  name: string;
  email: string | null;
  contact_number: string | null;
  country_code?: string | null;
  verification_request_experience_in_months?: number;
};

const formatYM = (months?: number) => {
  if (!months && months !== 0) return "-";
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const yStr = years ? `${years} Y` : "";
  const mStr = rem ? `${rem} Mo` : years ? "" : "0 Mo";
  return [yStr, mStr].filter(Boolean).join(" ");
};

const VerificationRequests = () => {
  const [rows, setRows] = useState<CoachVR[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const loadingRef = useRef(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStatus, setCurrentStatus] = useState<
    "pending" | "rejected" | "approved"
  >("pending");

  const fetchPage = useCallback(
    async (nextPage: number, reset = false) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      const data = await getCoachVerificationRequests(
        nextPage,
        "",
        undefined,
        currentStatus
      );
      const list = data?.coaches || [];
      setRows((prev) => (reset ? list : [...prev, ...list]));
      setTotalCount(data?.total_count || 0);
      setPage(nextPage);
      loadingRef.current = false;
    },
    [currentStatus]
  );

  useEffect(() => {
    setRows([]);
    fetchPage(1, true);
  }, [fetchPage]);

  // Ensure back button has a fallback destination when landing without state
  useEffect(() => {
    const state =
      (location.state as { path?: string; currentPage?: number }) || {};
    if (!state.path) {
      navigate(".", {
        replace: true,
        state: { path: "/coaches", currentPage: 1 },
      });
    }
  }, [location, navigate]);

  const onScrollEndRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const root = document.querySelector(".scroll-y");
    const observer = new IntersectionObserver(
      ([entry]) => {
        const totalPages = Math.ceil(totalCount / 10) || 1;
        if (entry.isIntersecting && page < totalPages && !loadingRef.current) {
          fetchPage(page + 1);
        }
      },
      { root: root as Element | null, rootMargin: "100px" }
    );
    const el = onScrollEndRef.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, [page, totalCount, fetchPage]);

  const onChangeTab = useCallback(
    (status: "pending" | "rejected" | "approved") => {
      if (currentStatus === status) return;
      setCurrentStatus(status);
      setSelectionMode(false);
      setSelectedIds(new Set());
    },
    [currentStatus]
  );

  useEffect(() => {
    // When status changes, reset and fetch first page with the new status
    setRows([]);
    fetchPage(1, true);
  }, [currentStatus, fetchPage]);

  const columns = useMemo(() => {
    const base = [
      { dataField: "name", title: "Coach Name" },
      { dataField: "email", title: "Email ID" },
      {
        title: "Contact No.",
        formatter: (r: CoachVR) =>
          r.country_code && r.contact_number
            ? `${r.country_code} ${r.contact_number}`
            : r.contact_number || "-",
      },
      {
        dataField: "verification_request_experience_in_months",
        title: "Experience",
        formatter: (r: CoachVR) =>
          formatYM(r.verification_request_experience_in_months),
      },
    ];
    if (!selectionMode) return base;
    return [
      ...base,
      {
        title: "",
        formatter: (r: CoachVR) => (
          <input
            type="checkbox"
            checked={selectedIds.has(r.id)}
            onChange={(e) => {
              const next = new Set(selectedIds);
              if (e.target.checked) next.add(r.id);
              else next.delete(r.id);
              setSelectedIds(next);
            }}
          />
        ),
      },
    ];
  }, [selectionMode, selectedIds]);

  const handleApproveReject = useCallback(
    async (status: "approved" | "rejected") => {
      if (!selectedIds.size) return;
      await updateCoachVerificationRequests(Array.from(selectedIds), status);
      setSelectedIds(new Set());
      setSelectionMode(false);
      setRows([]);
      await fetchPage(1, true);
    },
    [selectedIds, fetchPage]
  );

  return (
    <div className="ss-page-content d-flex flex-column flex-1 scroll-y bg-white">
      <HeaderComponent title="Coach Verification" shouldShowNav={true} />
      {/* Tabs and Controls */}
      <div className="d-flex justify-content-between align-items-center px-4 py-3">
        <div className="d-flex gap-2">
          <Button
            variant={
              currentStatus === "pending" ? "primary" : "outline-secondary"
            }
            onClick={() => onChangeTab("pending")}
          >
            Pending
          </Button>
          <Button
            variant={
              currentStatus === "rejected" ? "primary" : "outline-secondary"
            }
            onClick={() => onChangeTab("rejected")}
          >
            Rejected
          </Button>
          <Button
            variant={
              currentStatus === "approved" ? "primary" : "outline-secondary"
            }
            onClick={() => onChangeTab("approved")}
          >
            Approved
          </Button>
        </div>
        <div className="d-flex justify-content-end px-4 py-3">
          {!selectionMode ? (
            <Button variant="primary" onClick={() => setSelectionMode(true)}>
              Mark Eligibility
            </Button>
          ) : (
            <div className="d-flex gap-2">
              {(currentStatus === "pending" ||
                currentStatus === "rejected") && (
                <Button
                  variant="success"
                  disabled={!selectedIds.size}
                  onClick={() => handleApproveReject("approved")}
                >
                  Approve
                </Button>
              )}
              {(currentStatus === "pending" ||
                currentStatus === "approved") && (
                <Button
                  variant="danger"
                  disabled={!selectedIds.size}
                  onClick={() => handleApproveReject("rejected")}
                >
                  Reject
                </Button>
              )}
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setSelectedIds(new Set());
                  setSelectionMode(false);
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 scroll-y px-4 mb-3">
        <div className="table-responsive">
          <TableComponent
            columns={columns as any}
            tableData={rows}
            isClickAble={!selectionMode}
            onRowClick={
              !selectionMode
                ? (r: any) =>
                    navigate(`/coaches/verification_requests/${r.id}`, {
                      state: {
                        currentPage: page,
                        path: "/coaches/verification_requests",
                      },
                    })
                : undefined
            }
          />
          <div ref={onScrollEndRef as any} style={{ height: "1px" }} />
        </div>
      </div>
    </div>
  );
};

export default VerificationRequests;
